<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CardComponent from '@/components/cards/CardComponent.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import { tenderReportService } from '@/services/tenderReportService'
import { HardwareInstallationReportTemplateGenerator } from '@/utils/templateGenerator'
import { useAuthStore } from '@/stores/auth'
import ConsigneeDocumentUpload from '../Modals/ConsigneeDocumentUpload.vue'
import { notificationService } from '@/services/notificationService'
import api from '@/services/api'
import { AxiosError } from 'axios'
import {
  EngineerIcon,
  UploadIcon,
  TrashIcon,
  FileWaveIcon,
  FileExcleIcon,
  ImageIcon,
  FilePdfIcon,
  FileWordIcon
} from '@/icons'
import DownloadIcon from '@/icons/DownloadIcon.vue'
import { useSidebar } from '@/composables/useSidebar'

// ========== TYPE DEFINITIONS ==========
interface EngineerUser {
  id: string
  first_name?: string
  last_name?: string
  email?: string
  userType?: string
}

interface ReportCardItemGroup {
  id?: number
  fieldValues: {
    monitorSerialNumber?: string
    cpuSerialNumber?: string
    serialNumberCommon?: string
    installationStatus?: string
    remarks?: string
  }
}

interface ReportCard {
  id: number
  title: string
  itemName: string
  quantity: number
  visibleFields: {
    monitorSerialNumber?: boolean
    cpuSerialNumber?: boolean
    serialNumberCommon?: boolean
    installationStatus?: boolean
    remarks?: boolean
  }
  itemGroups: ReportCardItemGroup[]
  warrantyYears?: number
  warrantyMonths?: number
}

interface Document {
  id: string
  fileName: string
  fileSize: number
  fileType: string
  fileUrl: string
  description?: string
  uploadedBy: string
  uploadedByName: string
  uploadedAt: string
}

interface Consignee {
  id?: number
  name: string
  designation: string
  number: string
  assignedEngineerId: string
  assignedEngineerName: string
  callAlignedDate: string
  emailId: string
  faxNumber: string
  reportCards: ReportCard[]
  isCompleted?: boolean
  completionNotes?: string
  completedAt?: string
  documents?: Document[]
}

interface ReportData {
  id?: string
  tenderId: string
  selectedTender?: string
  t247Id?: string
  company?: string
  referenceNumber?: string
  purchaseOrderNumber?: string
  reportDate?: string
  poDate?: string
  customerName?: string
  customerAddress?: string
  stateOffice?: string
  contactPersonName?: string
  contactPersonDesignation?: string
  contactPersonNumber?: string
  department?: string
  consignees: Consignee[]
}

interface ApiErrorResponse {
  message?: string
  error?: string
}

interface Props {
  tenderId: string
}

const props = defineProps<Props>()
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const { isExpanded, isHovered } = useSidebar()

const currentUser = computed(() => authStore.user)
const isAdmin = computed(() => currentUser.value?.userType === 'admin')
const isEngineer = computed(() => currentUser.value?.userType === 'engineer')
const currentEngineerId = computed(() => currentUser.value?.id)

// Engineers MUST have consigneeName in query — redirect to dashboard if missing
const pinnedConsigneeName = computed(() =>
  route.query.consigneeName ? String(route.query.consigneeName) : null
)

const loading = ref<boolean>(false)
const error = ref<string | null>(null)
const reportData = ref<ReportData | null>(null)
const isGeneratingPDF = ref<boolean>(false)

// Document upload modal state
const showUploadModal = ref<boolean>(false)
const selectedConsigneeForUpload = ref<Consignee | null>(null)
const existingDocuments = ref<Document[]>([])
const deletingDocId = ref<string | null>(null)

// ── Consignee selection modal state ─────────────────────────────────────────
const showConsigneeSelectModal = ref<boolean>(false)
const selectedConsigneeId = ref<number | null>(null)

console.log('🔍 TenderEngineerView received tenderId:', props.tenderId)

// ========== TOTAL ITEM COUNT ==========
const totalItemsAcrossAllReports = computed(() => {
  let total = 0
  displayConsignees.value.forEach(consignee => {
    consignee.reportCards.forEach(card => { total += card.itemGroups.length })
  })
  return total
})

const calculateConsigneeStatus = (consignee: Consignee) => {
  let totalItems = 0
  let installedItems = 0

  for (const card of consignee.reportCards || []) {
    for (const group of card.itemGroups || []) {
      totalItems++
      if (group.fieldValues.installationStatus === 'yes') installedItems++
    }
  }

  const progress = totalItems > 0 ? Math.round((installedItems / totalItems) * 100) : 0
  let status = 'pending'
  if (totalItems > 0) {
    if (installedItems === totalItems) status = 'completed'
    else if (installedItems > 0) status = 'in-progress'
  }

  return { totalItems, installedItems, progress, status }
}

const displayConsignees = computed(() => {
  if (!reportData.value?.consignees) return []

  const mapConsignee = (consignee: Consignee) => {
    const { totalItems, installedItems, progress, status } = calculateConsigneeStatus(consignee)
    return {
      ...consignee,
      totalItems,
      installedItems,
      progress,
      installationStatus: consignee.isCompleted ? 'completed' : status
    }
  }

  if (isAdmin.value) {
    return reportData.value.consignees.map(mapConsignee)
  }

  if (isEngineer.value && pinnedConsigneeName.value) {
    return reportData.value.consignees
      .filter((c: Consignee) =>
        c.assignedEngineerId === currentEngineerId.value &&
        c.name === pinnedConsigneeName.value
      )
      .map(mapConsignee)
  }

  return []
})

const consigneesWithReports = computed(() =>
  displayConsignees.value.filter((c: Consignee) => c.reportCards.length > 0)
)

const selectConsignee = (id: number | null) => {
  selectedConsigneeId.value = id
}

const formatDate = (dateValue: string | null | undefined): string => {
  if (!dateValue) return 'N/A'
  try {
    const date = new Date(dateValue)
    if (isNaN(date.getTime())) return dateValue
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return 'N/A'
  }
}

// ========== DOCUMENT FUNCTIONS ==========
const getFileIcon = (fileType: string): Component => {
  const type = fileType.toLowerCase()

  if (type.includes('pdf')) {
    return FilePdfIcon
  }

  if (type.includes('word') ||
      type.includes('doc') ||
      type.includes('msword') ||
      type.includes('document')) {
    return FileWordIcon
  }

  if (type.includes('excel') ||
      type.includes('sheet') ||
      type.includes('xls') ||
      type.includes('spreadsheet') ||
      type.includes('ms-excel')) {
    return FileExcleIcon
  }

  if (type.includes('jpg') ||
      type.includes('jpeg') ||
      type.includes('png') ||
      type.includes('gif') ||
      type.includes('bmp') ||
      type.includes('image')) {
    return ImageIcon
  }

  return FileWaveIcon
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

const downloadDocument = async (documentId: string) => {
  try {
    const response = await api.get(`/api/tenders/documents/${documentId}/download`, {
      params: { tenderId: props.tenderId },
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'document.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading document:', error);
    notificationService.error('Failed to download document');
  }
};

const deleteDocument = async (documentId: string, consigneeName: string) => {
  if (!confirm('Are you sure you want to delete this document? This will permanently remove the file.')) return;

  deletingDocId.value = documentId;

  try {
    const response = await api.delete(`/api/tenders/documents/${documentId}`, {
      data: {
        tenderId: props.tenderId,
        consigneeName: consigneeName
      }
    });

    if (response.data.success) {
      notificationService.success('Document deleted successfully');
      await fetchEngineerData();
    } else {
      throw new Error(response.data.message || 'Delete failed');
    }
  } catch (error: unknown) {
    console.error('Error deleting document:', error);

    let errorMessage = 'Failed to delete document';

    if (error instanceof AxiosError) {
      const errorData = error.response?.data as ApiErrorResponse;
      errorMessage = errorData?.message || error.message || 'Failed to delete document';
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    notificationService.error(errorMessage);
  } finally {
    deletingDocId.value = null;
  }
};

// Open upload modal for the selected consignee
const openUploadModal = () => {
  // For engineer view, there's only one consignee
  if (displayConsignees.value.length === 1) {
    selectedConsigneeForUpload.value = displayConsignees.value[0]
    if (selectedConsigneeForUpload.value.documents) {
      existingDocuments.value = selectedConsigneeForUpload.value.documents
    } else {
      existingDocuments.value = []
    }
    showUploadModal.value = true
  } else {
    notificationService.warning('Please select a consignee to upload documents')
  }
}

const handleDocumentsUploaded = () => {
  showUploadModal.value = false
  fetchEngineerData()
}

// ========== PDF GENERATION ==========
const doGeneratePDF = async (consigneesToInclude: Consignee[]): Promise<void> => {
  if (!reportData.value) return

  const visibleFieldsConfig: Record<string, boolean> = {
    monitorSerialNumber: false, cpuSerialNumber: false,
    serialNumberCommon: false, installationStatus: false, remarks: false
  }
  consigneesToInclude.forEach(consignee => {
    consignee.reportCards.forEach(card => {
      Object.keys(card.visibleFields).forEach(key => {
        if ((card.visibleFields as Record<string, boolean>)[key]) visibleFieldsConfig[key] = true
      })
    })
  })

  const pdfData = {
    company: reportData.value.company || '',
    selectedCompanyProfile: reportData.value.company || '',
    companyProfileName: reportData.value.company || '',
    companyAddress: reportData.value.customerAddress || '',
    tenderRefNo: reportData.value.referenceNumber || reportData.value.t247Id || '',
    date: reportData.value.reportDate || '',
    signatoryName: reportData.value.contactPersonName || '',
    designation: reportData.value.contactPersonDesignation || '',
    signatureDate: reportData.value.reportDate || '',
    purchaseOrderNumber: reportData.value.purchaseOrderNumber || '',
    poDate: reportData.value.poDate || '',
    customerName: reportData.value.customerName || '',
    customerAddress: reportData.value.customerAddress || '',
    stateOffice: reportData.value.stateOffice || '',
    contactPersonName: reportData.value.contactPersonName || '',
    contactPersonDesignation: reportData.value.contactPersonDesignation || '',
    contactPersonNumber: reportData.value.contactPersonNumber || '',
    department: reportData.value.department || '',
    consignees: consigneesToInclude.map(c => ({
      name: c.name, designation: c.designation, number: c.number,
      assignedEngineerId: c.assignedEngineerId, assignedEngineerName: c.assignedEngineerName,
      callAlignedDate: c.callAlignedDate, emailId: c.emailId, faxNumber: c.faxNumber,
      reportCards: c.reportCards.map(card => ({
        id: card.id, title: card.title, itemName: card.itemName, quantity: card.quantity,
        visibleFields: card.visibleFields,
        warrantyYears: card.warrantyYears,
        warrantyMonths: card.warrantyMonths,
        itemGroups: card.itemGroups.map(group => ({
          fieldValues: {
            monitorSerialNumber: group.fieldValues.monitorSerialNumber || '',
            cpuSerialNumber: group.fieldValues.cpuSerialNumber || '',
            serialNumberCommon: group.fieldValues.serialNumberCommon || '',
            installationStatus: group.fieldValues.installationStatus || '',
            remarks: group.fieldValues.remarks || ''
          }
        }))
      }))
    })),
    visibleFieldsConfig
  }

  await HardwareInstallationReportTemplateGenerator.generateHardwareInstallationPDF(pdfData)
}

const generatePDF = async (): Promise<void> => {
  if (consigneesWithReports.value.length === 0) {
    notificationService.warning('No report templates found to generate PDF')
    return
  }
  if (isGeneratingPDF.value) return

  if (consigneesWithReports.value.length <= 1) {
    try {
      isGeneratingPDF.value = true
      await doGeneratePDF(consigneesWithReports.value as Consignee[])
      notificationService.success('PDF generated successfully')
    } catch (e) {
      console.error('PDF generation error:', e)
      notificationService.error('Failed to generate PDF')
    } finally {
      isGeneratingPDF.value = false
    }
    return
  }

  selectedConsigneeId.value = null
  showConsigneeSelectModal.value = true
}

const confirmConsigneeSelection = async (): Promise<void> => {
  showConsigneeSelectModal.value = false

  const toInclude = (
    selectedConsigneeId.value === null
      ? consigneesWithReports.value
      : consigneesWithReports.value.filter((c: Consignee) => c.id === selectedConsigneeId.value)
  ) as Consignee[]

  if (toInclude.length === 0) return

  try {
    isGeneratingPDF.value = true
    await doGeneratePDF(toInclude)
    const label = toInclude.length === 1
      ? `Report for ${toInclude[0].name || 'Consignee'}`
      : `Report for ${toInclude.length} consignees`
    notificationService.success(`${label} generated successfully`)
  } catch (e) {
    console.error('PDF generation error:', e)
    notificationService.error('Failed to generate PDF')
  } finally {
    isGeneratingPDF.value = false
  }
}

const fetchEngineerData = async (): Promise<void> => {
  if (!props.tenderId) {
    console.log('❌ No tenderId provided to TenderEngineerView')
    return
  }

  loading.value = true
  error.value = null

  try {
    console.log('🔍 Fetching report for tender UUID:', props.tenderId)
    const response = await tenderReportService.getReportByTenderId(props.tenderId) as ReportData | null
    console.log('📥 Report response:', response)

    if (response?.consignees) {
      response.consignees.forEach((consignee, index) => {
        console.log(`Consignee ${index} (${consignee.name}) documents:`, consignee.documents?.length || 0);
        if (consignee.documents && consignee.documents.length > 0) {
          console.log(`  Documents:`, consignee.documents);
        }
      });
    }

    if (response) {
      reportData.value = response
    } else {
      reportData.value = null
    }
  } catch (err) {
    console.error('❌ Error fetching engineer data:', err)
    error.value = 'Failed to load assigned consignee data'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const user = authStore.user as EngineerUser
  console.log('🔍 Current User:', {
    id: user?.id,
    name: user?.first_name + ' ' + user?.last_name,
    email: user?.email,
    userType: user?.userType
  })

  if (isEngineer.value && !pinnedConsigneeName.value) {
    router.replace({ name: 'engineer-dashboard' })
    return
  }

  fetchEngineerData()
})
</script>

<template>
  <div :class="!isAdmin ? 'mb-25' : ''" class="space-y-6">
    <!-- Fixed Action Bar -->
    <div
      v-if="!isAdmin"
      :class="['flex justify-between gap-3 mb-0 p-4 md:p-6 fixed bottom-0 right-0 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-60', isExpanded || isHovered ? 'lg:left-(--sidebar-max) lg:max-w-[calc(100%_-_var(--sidebar-max))]' : 'lg:left-(--sidebar-min) lg:max-w-[calc(100%_-_var(--sidebar-min))]']"
    >
      <div class="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium mr-2">
        <span>Total Items: {{ totalItemsAcrossAllReports }}</span>
      </div>
      <div class="flex gap-3">
        <BaseButton
          v-if="displayConsignees.length === 1"
          text="Upload Documents"
          :icon="UploadIcon"
          size="sm"
          variant="secondary"
          @click="openUploadModal"
        />
        <BaseButton
          text="Generate PDF"
          :icon="FilePdfIcon"
          size="sm"
          variant="secondary"
          :loading="isGeneratingPDF"
          :disabled="isGeneratingPDF || loading"
          @click="generatePDF"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <LoaderDefault title="Loading consignee data..." />
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-12">
      <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
        <div class="text-red-600 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Failed to Load Data</h3>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <BaseButton @click="fetchEngineerData" text="Try Again" size="sm" />
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="!reportData" class="text-center py-12">
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
        <div class="text-yellow-600 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 20 20">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Report Found</h3>
        <p class="text-gray-600">No installation report has been created for this tender yet.</p>
      </div>
    </div>

    <!-- No Consignees State -->
    <div v-else-if="displayConsignees.length === 0" class="text-center py-12">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
        <div class="text-blue-600 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          {{ isAdmin ? 'No Consignees Found' : 'No Assigned Consignees' }}
        </h3>
        <p class="text-gray-600">
          {{ isAdmin
            ? 'No consignees have been created for this tender yet.'
            : "You haven't been assigned to any consignee for this tender yet."
          }}
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Tender Details Card -->
      <CardComponent
        title="Tender Details"
        :showHeaderBorder="true"
        :showHeaderPaddingBottom="true"
        headerClass="border-l-4 border-l-blue-500 sticky top-[60px]"
        bodyClass="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <template #body>
          <div class="space-y-2">
            <label class="text-sm font-medium">Reference Number</label>
            <p class="text-sm py-2 rounded">{{ reportData.referenceNumber || 'N/A' }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">Purchase Order Number</label>
            <p class="text-sm py-2 rounded">{{ reportData.purchaseOrderNumber || 'N/A' }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">Report Date</label>
            <p class="text-sm py-2 rounded">{{ formatDate(reportData.reportDate) }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">PO Date</label>
            <p class="text-sm py-2 rounded">{{ formatDate(reportData.poDate) }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">Customer Name</label>
            <p class="text-sm py-2 rounded">{{ reportData.customerName || 'N/A' }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">Customer Address</label>
            <p class="text-sm py-2 rounded">{{ reportData.customerAddress || 'N/A' }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">State Office</label>
            <p class="text-sm py-2 rounded">{{ reportData.stateOffice || 'N/A' }}</p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">Contact Person</label>
            <p class="text-sm py-2 rounded">
              {{ reportData.contactPersonName }} - {{ reportData.contactPersonDesignation }}<br />
              {{ reportData.contactPersonNumber }}
            </p>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-medium">Department</label>
            <p class="text-sm py-2 rounded">{{ reportData.department || 'N/A' }}</p>
          </div>
        </template>
      </CardComponent>

      <!-- Consignees -->
      <div v-for="consignee in displayConsignees" :key="consignee.name" class="space-y-4">
        <!-- Consignee Card (removed upload button from header) -->
        <CardComponent
          :title="`Consignee: ${consignee.name}`"
          :subTitle="consignee.designation"
          :showHeaderBorder="true"
          :showHeaderPaddingBottom="true"
          headerClass="border-l-4 border-l-red-500 sticky top-[60px]"
          bodyClass="grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          <template #body>
            <div class="space-y-2">
              <label class="text-sm font-medium">Contact Number</label>
              <p class="text-sm py-2 rounded">{{ consignee.number || 'N/A' }}</p>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium">Email ID</label>
              <p class="text-sm py-2 rounded">{{ consignee.emailId || 'N/A' }}</p>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium">FAX Number</label>
              <p class="text-sm py-2 rounded">{{ consignee.faxNumber || 'N/A' }}</p>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium">Call Aligned Date</label>
              <p class="text-sm py-2 rounded">{{ formatDate(consignee.callAlignedDate) }}</p>
            </div>
          </template>
        </CardComponent>

        <!-- Report Cards for this Consignee -->
        <div v-for="card in consignee.reportCards" :key="card.id">
          <CardComponent
            :title="card.title"
            :subTitle="`${consignee.name} - ${consignee.designation}`"
            :showHeaderBorder="true"
            :showHeaderPaddingBottom="true"
            headerClass="border-l-4 border-l-green-500 sticky top-[60px]"
            bodyClass="grid grid-cols-1 gap-6"
            size="sm"
          >
            <template #body>
              <!-- Item Name and Config Section - Full width -->
              <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Item Name and Configuration</h5>
                <p class="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ card.itemName || 'N/A' }}</p>
                <div v-if="card.warrantyYears || card.warrantyMonths" class="mt-2 text-xs text-blue-600 dark:text-blue-400">
                  Warranty: {{ card.warrantyYears || 0 }} Year(s) {{ card.warrantyMonths || 0 }} Month(s)
                </div>
              </div>

              <!-- Data Table -->
              <DataTable
                :value="card.itemGroups"
                class="w-full text-sm"
                showGridlines
                stripedRows
                size="small"
                :pt="{
                  root: { class: 'border border-gray-200 dark:border-gray-800 rounded-md' },
                  thead: { class: 'bg-gray-50 dark:bg-gray-800' },
                  th: { class: 'px-3 py-2 font-medium text-xs dark:text-gray-300' },
                  td: { class: 'px-3 py-2' }
                }"
              >
                <Column v-if="card.visibleFields.monitorSerialNumber" field="fieldValues.monitorSerialNumber" header="Monitor Serial Number">
                  <template #body="{ data }">
                    <p class="text-sm">{{ data.fieldValues.monitorSerialNumber || '-' }}</p>
                  </template>
                </Column>
                <Column v-if="card.visibleFields.cpuSerialNumber" field="fieldValues.cpuSerialNumber" header="CPU Serial Number">
                  <template #body="{ data }">
                    <p class="text-sm">{{ data.fieldValues.cpuSerialNumber || '-' }}</p>
                  </template>
                </Column>
                <Column v-if="card.visibleFields.serialNumberCommon" field="fieldValues.serialNumberCommon" header="Serial Number (Common)">
                  <template #body="{ data }">
                    <p class="text-sm">{{ data.fieldValues.serialNumberCommon || '-' }}</p>
                  </template>
                </Column>
                <Column v-if="card.visibleFields.installationStatus" field="fieldValues.installationStatus" header="Status">
                  <template #body="{ data }">
                    <Badge
                      :value="data.fieldValues.installationStatus === 'yes' ? 'Installed' : 'Pending'"
                      :severity="data.fieldValues.installationStatus === 'yes' ? 'success' : 'warning'"
                      class="text-xs"
                    />
                  </template>
                </Column>
                <Column v-if="card.visibleFields.remarks" field="fieldValues.remarks" header="Remarks">
                  <template #body="{ data }">
                    <p class="text-sm">{{ data.fieldValues.remarks || '-' }}</p>
                  </template>
                </Column>
              </DataTable>
            </template>
          </CardComponent>
        </div>

        <!-- Documents Card (separate card after report templates) -->
        <CardComponent
          v-if="consignee.documents && consignee.documents.length > 0"
          title="Uploaded Documents"
          :showHeaderBorder="true"
          :showHeaderPaddingBottom="true"
          headerClass="border-l-4 border-l-purple-500 sticky top-[60px]"
          bodyClass="grid grid-cols-1 gap-3"
          size="sm"
        >
          <template #body>
            <div
              v-for="doc in consignee.documents"
              :key="doc.id"
              class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-sm transition-shadow"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <component
                  :is="getFileIcon(doc.fileType)"
                  class="w-8 h-8 mt-1"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {{ doc.fileName }}
                  </p>
                  <p class="text-xs text-gray-400">
                    {{ formatFileSize(doc.fileSize) }} • Uploaded {{ formatRelativeTime(doc.uploadedAt) }}
                  </p>
                  <p v-if="doc.uploadedByName" class="text-xs text-gray-500">
                    By: {{ doc.uploadedByName }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <BaseButton
                  @click="downloadDocument(doc.id)"
                  text="Download"
                  :icon="DownloadIcon"
                  size="xs"
                  variant="secondary"
                />
                <BaseButton
                  v-if="!isAdmin"
                  @click="deleteDocument(doc.id, consignee.name)"
                  :icon="TrashIcon"
                  size="xs"
                  variant="danger"
                  :loading="deletingDocId === doc.id"
                />
              </div>
            </div>
          </template>
        </CardComponent>
      </div>
    </div>

    <!-- Document Upload Modal -->
    <ConsigneeDocumentUpload
      v-if="selectedConsigneeForUpload"
      v-model:visible="showUploadModal"
      :tenderId="props.tenderId"
      :consignee="selectedConsigneeForUpload"
      :existingDocuments="existingDocuments"
      @documents-uploaded="handleDocumentsUploaded"
    />

    <!-- ── Consignee Selection Modal (admin only, shown when >1 consignee) ──── -->
    <ModalDefault
      v-model="showConsigneeSelectModal"
      title="Select Consignee for PDF"
      width="max-w-md"
      classBody="my-2"
    >
      <div class="space-y-2">
        <!-- All Consignees option -->
        <div
          class="flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors"
          :class="selectedConsigneeId === null
            ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
            : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700'"
          @click="selectConsignee(null)"
        >
          <input type="radio" :checked="selectedConsigneeId === null" @click.stop class="accent-blue-600" />
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-gray-800 dark:text-gray-200">All Consignees</span>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Download a single PDF containing all {{ consigneesWithReports.length }} consignees
            </p>
          </div>
        </div>

        <!-- Divider -->
        <div class="flex items-center gap-2 py-1">
          <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
          <span class="text-xs text-gray-400">or select one</span>
          <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
        </div>

        <!-- Individual consignees -->
        <div
          v-for="(consignee, idx) in consigneesWithReports"
          :key="consignee.name"
          class="flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors"
          :class="selectedConsigneeId === (consignee.id || idx)
            ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
            : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700'"
          @click="selectConsignee(consignee.id || idx)"
        >
          <input type="radio" :checked="selectedConsigneeId === (consignee.id || idx)" @click.stop class="accent-blue-600" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">#{{ idx + 1 }}</span>
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                {{ consignee.name || 'Unnamed Consignee' }}
              </span>
            </div>
            <div class="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              <span v-if="consignee.designation">{{ consignee.designation }}</span>
              <span v-if="consignee.assignedEngineerName" class="flex items-center gap-1">
                <EngineerIcon class="w-3 h-3" />
                {{ consignee.assignedEngineerName }}
              </span>
              <span class="text-blue-600 dark:text-blue-400">
                {{ consignee.reportCards.length }} template{{ consignee.reportCards.length !== 1 ? 's' : '' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <BaseButton text="Cancel" variant="secondary" @click="showConsigneeSelectModal = false" />
          <BaseButton
            :text="selectedConsigneeId === null ? `Download All (${consigneesWithReports.length})` : `Download Selected`"
            :icon="FilePdfIcon"
            variant="primary"
            @click="confirmConsigneeSelection"
          />
        </div>
      </template>
    </ModalDefault>
  </div>
</template>
